'use client';

/**
 * Cookie Consent Provider
 *
 * Manages cookie consent state for the application. Non-essential cookies
 * (analytics, marketing) must not be set until the user grants consent.
 *
 * Consent choices are stored in localStorage under 'cookie-consent'.
 * The provider exposes consent state and update functions via React context.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

const STORAGE_KEY = 'cookie-consent';

export interface CookieConsentPreferences {
  /** Essential cookies — always true, cannot be disabled */
  essential: true;
  /** Analytics cookies (PostHog) */
  analytics: boolean;
}

interface CookieConsentState {
  /** Whether the user has made a consent choice */
  hasConsented: boolean;
  /** Current consent preferences */
  preferences: CookieConsentPreferences;
  /**
   * True while the user is inside the authenticated application (set by the
   * `(app)` layout). In-app, product analytics is covered by the account
   * agreement rather than the public cookie banner — so the banner is
   * suppressed and analytics may initialize without a banner choice.
   */
  isAppSession: boolean;
  /** Accept all non-essential cookies */
  acceptAll: () => void;
  /** Reject all non-essential cookies */
  rejectAll: () => void;
  /** Update specific consent preferences */
  updatePreferences: (
    prefs: Partial<Omit<CookieConsentPreferences, 'essential'>>
  ) => void;
  /** Mark/unmark the current session as an authenticated in-app session. */
  setAppSession: (value: boolean) => void;
}

const defaultPreferences: CookieConsentPreferences = {
  essential: true,
  analytics: false
};

const CookieConsentContext = createContext<CookieConsentState>({
  hasConsented: false,
  preferences: defaultPreferences,
  isAppSession: false,
  acceptAll: () => {},
  rejectAll: () => {},
  updatePreferences: () => {},
  setAppSession: () => {}
});

function loadStoredConsent(): {
  hasConsented: boolean;
  preferences: CookieConsentPreferences;
} {
  if (typeof window === 'undefined') {
    return { hasConsented: false, preferences: defaultPreferences };
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return { hasConsented: false, preferences: defaultPreferences };
    }

    const parsed = JSON.parse(stored);
    return {
      hasConsented: true,
      preferences: {
        essential: true,
        analytics: Boolean(parsed.analytics)
      }
    };
  } catch {
    return { hasConsented: false, preferences: defaultPreferences };
  }
}

function saveConsent(preferences: CookieConsentPreferences) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        analytics: preferences.analytics,
        timestamp: new Date().toISOString()
      })
    );
  } catch {
    // localStorage may be unavailable in some contexts
  }
}

export function CookieConsentProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [hasConsented, setHasConsented] = useState(false);
  const [preferences, setPreferences] =
    useState<CookieConsentPreferences>(defaultPreferences);
  const [isAppSession, setIsAppSession] = useState(false);

  useEffect(() => {
    const stored = loadStoredConsent();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional effect-driven state sync (prop/selection/timer); not derivable during render
    setHasConsented(stored.hasConsented);
    setPreferences(stored.preferences);
  }, []);

  const acceptAll = useCallback(() => {
    const newPrefs: CookieConsentPreferences = {
      essential: true,
      analytics: true
    };
    setPreferences(newPrefs);
    setHasConsented(true);
    saveConsent(newPrefs);
  }, []);

  const rejectAll = useCallback(() => {
    const newPrefs: CookieConsentPreferences = {
      essential: true,
      analytics: false
    };
    setPreferences(newPrefs);
    setHasConsented(true);
    saveConsent(newPrefs);
  }, []);

  const updatePreferences = useCallback(
    (prefs: Partial<Omit<CookieConsentPreferences, 'essential'>>) => {
      setPreferences((current) => {
        const newPrefs: CookieConsentPreferences = {
          ...current,
          ...prefs,
          essential: true
        };
        setHasConsented(true);
        saveConsent(newPrefs);
        return newPrefs;
      });
    },
    []
  );

  const setAppSession = useCallback((value: boolean) => {
    setIsAppSession(value);
  }, []);

  const value = useMemo(
    () => ({
      hasConsented,
      preferences,
      isAppSession,
      acceptAll,
      rejectAll,
      updatePreferences,
      setAppSession
    }),
    [
      hasConsented,
      preferences,
      isAppSession,
      acceptAll,
      rejectAll,
      updatePreferences,
      setAppSession
    ]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}
